import React, { useMemo, useCallback } from 'react';
import { useShallowEqualSelector } from '../../../../../hooks/redux';

import { ReduxState } from '../../../../../redux/reducers';
import { SettingsModelTypeCourseWithStaff } from '../../../../../interfaces/merchants/resources/books';

import {
  finalStep,
  goTo,
  nextStep,
} from '../../../../../redux/actions/merchants/resources/books';
import { getMerchantResourcesBooks } from '../../../../../redux/selectors/merchants/resources/books';

import {
  StepItems,
  BookStepSettingPair,
} from '../../../../../molecules/merchant/resources/book/BooksStep';

import BookingWithPageDetail from '../BookingWithPageDetail';
import ReservationFlowAuthPage from '../ReservationFlowAuthPage';
import { InputPaymentOrganism } from '../InputPayment';
import InputBookingDateTime from '../InputBookingDateTime';
import InputBookingTargetWithoutStaff from '../InputBookingTargetWithoutStaff';
import InputBookingTargetWithStaff from '../InputBookingTargetWithStaff';
import InputCompletion from '../InputCompletion';
import InputCompletionFailed from '../InputCompletionFailed';
import InputConfirmation from '../InputConfirmation';
import InputUserInfo from '../InputUserInfo';
import { useIsRequirePayment } from '../../../../../hooks/booking-page';
import { BooksStepOrganism } from '../../../../reservation-flow/BooksStep';
import { useAppDispatch } from '../../../../../redux';

interface Props {
  isExpressFlow: boolean;
  merchantId: string;
  resourcePublicId: string;
  lineLiffId: string | undefined;
}

const FULL_WORKFLOW_STEPS = [
  StepItems.Booking,
  StepItems.SelectDate,
  StepItems.Customer,
  StepItems.Payment,
  StepItems.Confirmation,
];
const SKIP_PAYMENT_WORKFLOW_STEPS = [
  StepItems.Booking,
  StepItems.SelectDate,
  StepItems.Customer,
  StepItems.Confirmation,
];
const ERROR_REINPUT_PAYMENT_STEP = 97;
const ERROR_RESTART_STEP = 98;
const ERROR_RESELECT_STEP = 99;

const ModelTypeCourseWithStaff: React.FC<Props> = ({
  isExpressFlow,
  merchantId,
  resourcePublicId,
  lineLiffId,
}) => {
  const dispatch = useAppDispatch();

  const merchantResourcesBooks = useShallowEqualSelector((state: ReduxState) =>
    getMerchantResourcesBooks(state),
  );
  const { merchantResource, step, postable, renew } = merchantResourcesBooks;

  const settings = useMemo<SettingsModelTypeCourseWithStaff>(
    () => merchantResourcesBooks.settings as SettingsModelTypeCourseWithStaff,
    [merchantResourcesBooks],
  );
  const publicId = useMemo(
    () => merchantResourcesBooks.merchantId!,
    [merchantResourcesBooks],
  );

  const renderRequireLoginAt = useCallback(
    (step: number) => {
      return (
        <ReservationFlowAuthPage
          moveToNext={() => dispatch(nextStep(step))}
          onRestart={() => dispatch(goTo(ERROR_RESTART_STEP))}
          renew={renew}
          resourcePublicId={resourcePublicId}
        />
      );
    },
    [dispatch, renew, resourcePublicId],
  );

  const renderInputBookingTargetWithStaffAt = useCallback(
    (step: number) => {
      const { staffSelectable } = merchantResource!;

      if (isExpressFlow) {
        return (
          <BookingWithPageDetail
            merchantId={merchantId}
            merchantResource={merchantResource!}
            showPerformableStaffMembers={false}
          >
            {staffSelectable ? (
              <InputBookingTargetWithStaff
                modelType={settings.modelType}
                settings={settings.bookingTargets}
                postable={postable!}
                onSubmit={() => dispatch(nextStep(step))}
                onRestart={() => dispatch(goTo(ERROR_RESTART_STEP))}
                renew={renew}
                withExpressFlow={isExpressFlow}
                resourcePublicId={resourcePublicId}
              />
            ) : (
              <InputBookingTargetWithoutStaff
                modelType={settings.modelType}
                settings={settings.bookingTargets}
                postable={postable!}
                onSubmit={() => dispatch(nextStep(step))}
                onRestart={() => dispatch(goTo(ERROR_RESTART_STEP))}
                renew={renew}
                withExpressFlow={isExpressFlow}
                resourcePublicId={resourcePublicId}
              />
            )}
          </BookingWithPageDetail>
        );
      } else {
        return staffSelectable ? (
          <InputBookingTargetWithStaff
            modelType={settings.modelType}
            settings={settings.bookingTargets}
            postable={postable!}
            onSubmit={() => dispatch(nextStep(step))}
            onRestart={() => dispatch(goTo(ERROR_RESTART_STEP))}
            renew={renew}
            withExpressFlow={isExpressFlow}
            resourcePublicId={resourcePublicId}
          />
        ) : (
          <InputBookingTargetWithoutStaff
            modelType={settings.modelType}
            settings={settings.bookingTargets}
            postable={postable!}
            onSubmit={() => dispatch(nextStep(step))}
            onRestart={() => dispatch(goTo(ERROR_RESTART_STEP))}
            renew={renew}
            withExpressFlow={isExpressFlow}
            resourcePublicId={resourcePublicId}
          />
        );
      }
    },
    [
      dispatch,
      isExpressFlow,
      merchantId,
      merchantResource,
      postable,
      renew,
      resourcePublicId,
      settings,
    ],
  );

  const renderInputBookingDateTimeAt = useCallback(
    (step: number, modifierStep: number) => {
      return (
        <InputBookingDateTime
          settings={settings.bookingDateTimes}
          postable={postable!}
          onSubmit={() => dispatch(nextStep(step))}
          onRestart={() => dispatch(goTo(ERROR_RESTART_STEP))}
          backToModifyPage={() => dispatch(goTo(modifierStep))}
          resourcePublicId={resourcePublicId}
        />
      );
    },
    [settings, postable, dispatch, resourcePublicId],
  );

  const renderInputUserInfoAt = useCallback(
    (step: number, submitLabelIntlId: string) => {
      return (
        <InputUserInfo
          settings={settings.userInfo}
          onSubmit={() => dispatch(nextStep(step))}
          onRestart={() => dispatch(goTo(ERROR_RESTART_STEP))}
          merchantPublicId={merchantId}
          resourcePublicId={resourcePublicId}
          submitLabelIntlId={submitLabelIntlId}
        />
      );
    },
    [settings, dispatch, merchantId, resourcePublicId],
  );

  const renderInputPaymentAt = useCallback(
    (step: number) => {
      return (
        <InputPaymentOrganism
          settings={settings.payment!}
          merchantPublicId={publicId}
          postable={postable!}
          onSubmit={() => dispatch(nextStep(step))}
          onRestart={() => dispatch(goTo(ERROR_RESTART_STEP))}
        />
      );
    },
    [settings, publicId, postable, dispatch],
  );

  const renderInputConfirmationAt = useCallback(
    (step: number) => {
      return (
        <InputConfirmation
          merchantResource={{
            publicId: merchantResource!.publicId,
            name: merchantResource!.name,
            imageUrl: merchantResource!.primaryImage?.thumb?.url,
            modelType: merchantResource!.modelType,
          }}
          settings={settings.confirmation}
          postable={postable!}
          onSubmit={() => dispatch(finalStep(step))}
          onReinputPayment={() => dispatch(goTo(ERROR_REINPUT_PAYMENT_STEP))}
          onReselect={() => dispatch(goTo(ERROR_RESELECT_STEP))}
          onRestart={() => dispatch(goTo(ERROR_RESTART_STEP))}
          publicId={publicId}
          lineLiffId={lineLiffId}
        />
      );
    },
    [merchantResource, settings, postable, dispatch, publicId, lineLiffId],
  );

  const renderInputCompletion = useCallback(() => {
    return (
      <InputCompletion
        merchantResource={{
          publicId: merchantResource!.publicId,
          name: merchantResource!.name,
          imageUrl: merchantResource!.primaryImage?.thumb?.url,
          modelType: merchantResource!.modelType,
        }}
        settings={settings.completion!}
        publicId={publicId}
        withExpressFlow={isExpressFlow}
        resourcePublicId={resourcePublicId}
      />
    );
  }, [isExpressFlow, merchantResource, publicId, resourcePublicId, settings]);

  const renderFailedRestart = useCallback(() => {
    return (
      <InputCompletionFailed
        settings={settings.completion!}
        backToLabel={'books.submitLabel.restart'}
        backToModifyPage={() => location.reload()}
      />
    );
  }, [settings]);

  const renderFailedReselect = useCallback(
    (modifierStep: number) => {
      return (
        <InputCompletionFailed
          settings={settings.completion!}
          backToLabel={'books.submitLabel.reselect'}
          backToModifyPage={() => dispatch(goTo(modifierStep))}
        />
      );
    },
    [dispatch, settings],
  );

  const renderFailedReinput = useCallback(
    (modifierStep: number) => {
      return (
        <InputCompletionFailed
          settings={settings.completion!}
          backToLabel={'books.submitLabel.reinput'}
          backToModifyPage={() => dispatch(goTo(modifierStep))}
        />
      );
    },
    [dispatch, settings],
  );

  const fullWorkFlowSetting = useCallback((): BookStepSettingPair => {
    // prettier-ignore
    const setting = {
      0: { displayProgress: false, currentProgress: null,                   renderFn: () => renderRequireLoginAt(0)},
      1: { displayProgress: true,  currentProgress: StepItems.Booking,      renderFn: () => renderInputBookingTargetWithStaffAt(1)   },
      2: { displayProgress: true,  currentProgress: StepItems.SelectDate,   renderFn: () => renderInputBookingDateTimeAt(2, 1) },
      3: { displayProgress: true,  currentProgress: StepItems.Customer,     renderFn: () => renderInputUserInfoAt(3, 'books.submitLabel.payment') },
      4: { displayProgress: true,  currentProgress: StepItems.Payment,      renderFn: () => renderInputPaymentAt(4)         },
      5: { displayProgress: true,  currentProgress: StepItems.Confirmation, renderFn: () => renderInputConfirmationAt(5)    },
      6: { displayProgress: false, currentProgress: null,                   renderFn: () => renderInputCompletion()         },

      [ERROR_REINPUT_PAYMENT_STEP]: {
        displayProgress: true, currentProgress: StepItems.Confirmation, renderFn: () => renderFailedReinput(4) },
      [ERROR_RESTART_STEP]: {
        displayProgress: true, currentProgress: StepItems.Confirmation, renderFn: () => renderFailedRestart() },
      [ERROR_RESELECT_STEP]: {
        displayProgress: true, currentProgress: StepItems.Confirmation, renderFn: () => renderFailedReselect(2) },
    }[step!];

    return [FULL_WORKFLOW_STEPS, setting];
  }, [
    renderFailedReinput,
    renderFailedReselect,
    renderFailedRestart,
    renderInputBookingDateTimeAt,
    renderInputBookingTargetWithStaffAt,
    renderInputCompletion,
    renderInputConfirmationAt,
    renderInputPaymentAt,
    renderInputUserInfoAt,
    renderRequireLoginAt,
    step,
  ]);

  const skipPaymentFlowSetting = useCallback((): BookStepSettingPair => {
    // prettier-ignore
    const setting = {
      0: { displayProgress: false, currentProgress: null,                   renderFn: () => renderRequireLoginAt(0)},
      1: { displayProgress: true,  currentProgress: StepItems.Booking,      renderFn: () => renderInputBookingTargetWithStaffAt(1)},
      2: { displayProgress: true,  currentProgress: StepItems.SelectDate,   renderFn: () => renderInputBookingDateTimeAt(2, 1)},
      3: { displayProgress: true,  currentProgress: StepItems.Customer,     renderFn: () => renderInputUserInfoAt(3, 'books.submitLabel.confirm')},
      4: { displayProgress: true,  currentProgress: StepItems.Confirmation, renderFn: () => renderInputConfirmationAt(4)},
      5: { displayProgress: false, currentProgress: null,                   renderFn: () => renderInputCompletion()},

      [ERROR_RESTART_STEP]: {
        displayProgress: true, currentProgress: StepItems.Confirmation, renderFn: () => renderFailedRestart() },
      [ERROR_RESELECT_STEP]: {
        displayProgress: true, currentProgress: StepItems.Confirmation, renderFn: () => renderFailedReselect(2) },
    }[step!];

    return [SKIP_PAYMENT_WORKFLOW_STEPS, setting];
  }, [
    renderFailedReselect,
    renderFailedRestart,
    renderInputBookingDateTimeAt,
    renderInputBookingTargetWithStaffAt,
    renderInputCompletion,
    renderInputConfirmationAt,
    renderInputUserInfoAt,
    renderRequireLoginAt,
    step,
  ]);

  const isRequirePayment = useIsRequirePayment();

  const [allSteps, setting] = isRequirePayment
    ? fullWorkFlowSetting()
    : skipPaymentFlowSetting();
  return (
    <>
      {setting && setting.currentProgress && setting.displayProgress && (
        <BooksStepOrganism
          allSteps={allSteps}
          current={allSteps.indexOf(setting.currentProgress) + 1}
          resourcePublicId={merchantResource?.publicId ?? ''}
        />
      )}
      {setting && setting.renderFn()}
    </>
  );
};

export default ModelTypeCourseWithStaff;
